import { Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { PLATZHALTER_TEXT, PROJEKTDURCHFUERUNG } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { NonRequired, Required } from 'src/lib/validation/ValidationTypes';
import { InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { checkReadOnlyInUeberarbeitung, isTrue } from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const BesondereFoerderwuerdigkeit: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleChange = (path: string, value: any) => {
		const appCopy = cloneDeep(application);

		const keineBesonderePath = P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Keine_Besondere.path;
		const freitextfeldPath = P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Freitextfeld.path;

		const pathsToCheck = [
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Zielgruppen_Und_Institutionen.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Junge_Erwachsene.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Vulnerable_Gruppen.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_DAC_Liste.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Developed_Countries_Statt.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Innovativen_Charakter.path,
			P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Multiplikatorenwirkung.path,
		];

		if (path === keineBesonderePath && isTrue(value)) {
			pathsToCheck.forEach((pathToCheck) => {
				set(appCopy, pathToCheck, false);
			});
			set(appCopy, freitextfeldPath, null);
			set(appCopy, path, value);
			setApplicationProperty('attributes', get(appCopy, 'attributes', {}));
		} else if (pathsToCheck.includes(path) && isTrue(value)) {
			set(appCopy, keineBesonderePath, false);
			set(appCopy, path, value);
			setApplicationProperty('attributes', get(appCopy, 'attributes', {}));
		} else {
			set(appCopy, path, value);
			setApplicationProperty('attributes', get(appCopy, 'attributes', {}));
		}
	};
	return (
		<>
			<ApplicationHeading title='Besondere Förderwürdigkeit' description={PLATZHALTER_TEXT} />
			<InhaltWrapper marginTop='0'>
				<Typography>
					Ist Ihr Projekt besonders förderwürdig? Bitte kreuzen Sie zutreffende Optionen an und erläutern Sie dies kurz und prägnant.
				</Typography>
			</InhaltWrapper>
			<CreateForm formStruc={formStruc(application)} formValues={application} updateFormValuesFunc={handleChange} readOnly={updatedReadOnly} />
		</>
	);
};

export default BesondereFoerderwuerdigkeit;

const formStruc = (application: any) => {
	const projektdurchfuehrung = get(application, P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Projektdurchfuehrung.path);
	let struc: FormStrucItem[] = [];

	if (projektdurchfuehrung === PROJEKTDURCHFUERUNG.inland) {
		struc.push({
			fields: [
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Zielgruppen_Und_Institutionen,
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Junge_Erwachsene,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		});
	} else if (projektdurchfuehrung === PROJEKTDURCHFUERUNG.ausland) {
		struc.push({
			fields: [
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Vulnerable_Gruppen,
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_DAC_Liste,
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Developed_Countries_Statt,
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Innovativen_Charakter,
				P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Multiplikatorenwirkung,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		});
	}

	struc.push({
		fields: [
			{
				...P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Keine_Besondere,
			},
		],
	});

	// Prüfe ob mindestens ein Feld true ist
	const isAnyFieldChecked =
		(projektdurchfuehrung === PROJEKTDURCHFUERUNG.inland &&
			(isTrue(
				get(
					application,
					P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Zielgruppen_Und_Institutionen.path,
				),
			) ||
				isTrue(
					get(application, P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Inland_Junge_Erwachsene.path),
				))) ||
		(projektdurchfuehrung === PROJEKTDURCHFUERUNG.ausland &&
			(isTrue(
				get(application, P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Vulnerable_Gruppen.path),
			) ||
				isTrue(get(application, P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_DAC_Liste.path)) ||
				isTrue(
					get(
						application,
						P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Developed_Countries_Statt.path,
					),
				) ||
				isTrue(
					get(
						application,
						P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Innovativen_Charakter.path,
					),
				) ||
				isTrue(
					get(
						application,
						P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Ausland_Multiplikatorenwirkung.path,
					),
				)));

	if (isAnyFieldChecked) {
		struc.push({
			fields: [
				{
					...P.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Foerderwuerdigkeit_Freitextfeld,
					maxCharLength: 1500,
					validation: [{ validateFunction: isAnyFieldChecked ? Required : NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		});
	}

	return struc;
};

export const getFormStrucBesondereFoerderwuerdigkeit = (application: any) => {
	return concat(formStruc(application));
};
